import * as React from 'react';

import * as UI from 'core/ui';
import * as Images from 'pos/Images';

import { Res, AppointmentForEditionDTO, Permission, TicketLocationDTO, AppointmentStatus, TicketDTO } from 'core/api';
import { Result } from 'core/infrastructure';
import { Preconditions } from 'core/infrastructure-next';
import { Command } from 'pos/common';
import { createSaleCentersContent, createMapsContent, TableCompanionsIcon, SaleLocationStatus, PosStatelessScreenClassComponent, ScreenSize, ActionButtonDefinition } from 'pos/ui';
import { AppointmentFacade, RestaurantFacade } from 'pos/facades';
import { Flex, Cols, Col } from 'core/ui';
import { Message, Modal } from 'core/ui-next/modal';

const LocationStatus = SaleLocationStatus.create(RestaurantFacade.getRestaurantStatus);
const SaleCentersContent = createSaleCentersContent(LocationStatus);
const MapsContent = createMapsContent(LocationStatus);

type Props = Modal.Actions<TicketLocationDTO> & { appointment: AppointmentForEditionDTO };

class CloseAppointmentView extends PosStatelessScreenClassComponent<Props> {

	protected getScreenId(): string {
		return 'CLOSE_APPOINTMENT';
	}

	protected getTitle(): string {
		return Res.Web.CLOSE_APPOINTMENTS_TITLE();
	}

	protected getImage(): string | SvgIcon {
		return Images.Attend;
	}

	protected getSize(): ScreenSize {
		return 'full-screen';
	}

	protected getActionButtonsDefs(): Nullable<ActionButtonDefinition>[] {
		const mode = LocationStatus.getSelection().mode === 'maps'
			? { title: Res.Web.SELECT_SALE_LOCATION_SALE_CENTER_BTN(), image: Images.SaleCenter, action: LocationStatus.selectSaleCenter }
			: LocationStatus.getAllMaps().length ? { title: Res.Web.SELECT_SALE_LOCATION_MAPS_BTN(), image: Images.Map, action: LocationStatus.selectMap } : null;

		return [
			this.cancelButton(),
			null,
			null,
			null,
			null,
			mode
		];
	}

	onComponentDidMount(): void {
		LocationStatus.registerOnChange(async () => this.ifMounted(() => this.forceUpdate()));
	}

	onComponentWillUnmount(): void {
		LocationStatus.unregisterOnChanged();
	}

	private onSaleLocationSelected = async (location: TicketLocationDTO) => this.close(location);

	protected getContent(): React.ReactNode {
		const selection = LocationStatus.getSelection();

		return <Flex.Vertical>
			<Flex.Item className='text-18 border-bottom'>
				<Cols>
					<Col width='*'>{this.props.appointment.Customer.FiscalName}</Col>
					<Col><TableCompanionsIcon value={this.props.appointment.Guests} /></Col>
				</Cols>
				<Cols>
					<Col width='*'>
						{UI.Format.asLocationDescription({ FriendlyName: '', Location: this.props.appointment.Location })}</Col>
				</Cols>
			</Flex.Item>
			<Flex.Item size='*'>
				{selection.mode === 'maps'
					? <MapsContent selected={selection.selectedMap} onSaleLocationSelected={this.onSaleLocationSelected} />
					: <SaleCentersContent selected={selection.selectedSaleCenter} onSaleLocationSelected={this.onSaleLocationSelected} />}
			</Flex.Item>
		</Flex.Vertical>;
	}
}

async function canCloseAppointment(appointment: AppointmentForEditionDTO) {
	if (appointment.Status === AppointmentStatus.Pending)
		return true;

	const result = await Message.confirm(Res.Msg.MSG_APPOINTMENT_CONFIRM_CLOSE_APPOINTMET(AppointmentStatus.toString(appointment.Status)));

	return result.ok;
}

declare global {
	interface CommandType {
		closeAppointment: (appointment: AppointmentForEditionDTO) => Promise<Result<TicketDTO>>;
	}
}

Command.register('closeAppointment', Preconditions.and(Preconditions.ensure(Permission.Appointment_Close), Preconditions.ensureIsNotLite), async (appointment: AppointmentForEditionDTO): Promise<Result<TicketDTO>> => {

	const canClose = await canCloseAppointment(appointment);

	if (!canClose)
		return Result.error();

	await LocationStatus.initialize();

	const locationResult = await Modal.show(CloseAppointmentView, { appointment: appointment });

	if (!locationResult.ok)
		return Result.error();

	const ticket = await AppointmentFacade.close(appointment.Id, locationResult.value);

	return Result.ok(ticket);

});
